"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Flag, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/app/_module/utils/utils";
import { Button } from "@/app/_module/components/ui/button";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService, ForumComment, timeAgo, forumPath } from "./ForumService";
import { VoteControl } from "./VoteControl";
import { AuthorName } from "./KarmaHovercard";
import { ForumMarkdown } from "./ForumMarkdown";
import { ReportDialog } from "./ReportDialog";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { useForumAccess } from "../hooks/useForumAccess";

const RENDER_DEPTH_CAP = 6; // storage caps at 8; render collapses at 6 → "continue thread"

interface TreeNode extends ForumComment {
  children: TreeNode[];
}

// Flat list → tree. Roots ordered as the server sent them (already sorted);
// children sorted by score desc (Reddit default).
export function buildCommentTree(comments: ForumComment[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const c of comments) byId.set(c.id, { ...c, children: [] });
  const roots: TreeNode[] = [];
  for (const c of comments) {
    const node = byId.get(c.id)!;
    const parent = c.parentCommentId ? byId.get(c.parentCommentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortChildren = (n: TreeNode) => {
    n.children.sort((a, b) => b.score - a.score || a.createdAt.localeCompare(b.createdAt));
    n.children.forEach(sortChildren);
  };
  roots.forEach(sortChildren);
  return roots;
}

// ── Inline composer (top-level + replies) ───────────────────────────────────
export function CommentComposer({
  postId,
  parentCommentId,
  placeholder = "Share your take…",
  autoFocus,
  onDone,
}: {
  postId: string;
  parentCommentId?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  onDone?: () => void;
}) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { requireWrite, canWrite, eligibility } = useForumAccess();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const submit = () =>
    requireWrite(async () => {
      if (!body.trim()) return;
      setSubmitting(true);
      try {
        await forumService.createComment(postId, { body: body.trim(), parentCommentId });
        setBody("");
        queryClient.invalidateQueries({ queryKey: ["forum", "comments", postId] });
        queryClient.invalidateQueries({ queryKey: ["forum", "post"] });
        onDone?.();
      } catch (e: any) {
        toast({ title: "Comment failed", description: e.message, variant: "destructive", duration: 5000 });
      } finally {
        setSubmitting(false);
      }
    });

  return (
    <div className="space-y-1.5">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, 10_000))}
        placeholder={canWrite ? placeholder : eligibility?.reason === "FORUM_DEMO_READ_ONLY" ? "Demo accounts can read but not reply" : "An active subscription is required to comment"}
        rows={parentCommentId ? 2 : 3}
        autoFocus={autoFocus}
        className="text-sm resize-y min-h-[60px]"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">Markdown supported · ⌘↵ to post · no client info, no pitches</span>
        <div className="flex gap-1.5">
          {onDone && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onDone}>
              Cancel
            </Button>
          )}
          <Button size="sm" className="h-7 text-xs" onClick={submit} disabled={submitting || !body.trim()}>
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : parentCommentId ? "Reply" : "Comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Single comment node (recursive) ─────────────────────────────────────────
function CommentNode({
  node,
  postId,
  postSlug,
  postLocked,
}: {
  node: TreeNode;
  postId: string;
  postSlug: string;
  postLocked: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [replying, setReplying] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { requireWrite, isModerator } = useForumAccess();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const totalDescendants = useMemo(() => {
    let n = 0;
    const walk = (t: TreeNode) => {
      n += t.children.length;
      t.children.forEach(walk);
    };
    walk(node);
    return n;
  }, [node]);

  // Mod removal (someone else's comment) vs Reddit-style self-delete.
  const deletingAsMod = isModerator && !node.isOwn;

  const confirmDelete = async (reason?: string) => {
    try {
      if (deletingAsMod) {
        await forumService.mod.removeComment(node.id, reason || "Removed by moderator");
      } else {
        await forumService.deleteComment(node.id);
      }
      queryClient.invalidateQueries({ queryKey: ["forum", "comments", postId] });
      toast({ title: deletingAsMod ? "Comment removed" : "Comment deleted", duration: 2500 });
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    }
  };

  if (collapsed) {
    return (
      <div className="flex items-center gap-2 py-1 pl-1 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="inline-flex items-center gap-1 hover:text-primary"
        >
          <ChevronDown className="h-3 w-3 -rotate-90" />
          {node.author ? node.author.name : node.body}
          <span className="text-muted-foreground/70">
            · {totalDescendants > 0 ? `${totalDescendants + 1} comments` : "1 comment"}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* Thread line — clicking collapses (Reddit muscle memory) */}
        <button
          type="button"
          aria-label="Collapse thread"
          onClick={() => setCollapsed(true)}
          className="group/line flex w-4 shrink-0 justify-center"
        >
          <span className="w-0.5 rounded bg-border group-hover/line:bg-primary transition-colors" />
        </button>

        <div className="min-w-0 flex-1 pb-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            {node.author ? <AuthorName author={node.author} showAvatar /> : <span className="italic">{node.body}</span>}
            <span>{timeAgo(node.createdAt)}</span>
            {node.isEdited && <span className="italic">edited</span>}
          </div>

          {node.isRemoved ? (
            <p className="mt-0.5 text-xs italic text-muted-foreground">{node.body}</p>
          ) : (
            <ForumMarkdown className="mt-0.5">{node.body}</ForumMarkdown>
          )}

          <div className="mt-1 flex items-center gap-2">
            <VoteControl
              targetType="comment"
              targetId={node.id}
              score={node.score}
              myVote={node.myVote}
              orientation="horizontal"
              size="sm"
              disabled={node.isRemoved}
            />
            {!postLocked && !node.isRemoved && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] text-muted-foreground"
                onClick={() => requireWrite(() => setReplying((v) => !v))}
              >
                Reply
              </Button>
            )}
            {!node.isRemoved && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-destructive"
                onClick={() => requireWrite(() => setReportOpen(true))}
                aria-label="Report comment"
              >
                <Flag className="h-3 w-3" />
              </Button>
            )}
            {(node.isOwn || isModerator) && !node.isRemoved && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
                aria-label="Delete comment"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>

          {replying && (
            <div className="mt-2">
              <CommentComposer
                postId={postId}
                parentCommentId={node.id}
                placeholder={`Reply to ${node.author?.name || "comment"}…`}
                autoFocus
                onDone={() => setReplying(false)}
              />
            </div>
          )}

          {node.children.length > 0 &&
            (node.depth >= RENDER_DEPTH_CAP ? (
              <Link
                href={forumPath(`/t/${postSlug}/comment/${node.id}`)}
                className="mt-1 inline-block text-[11px] font-medium text-primary hover:underline"
              >
                Continue this thread →
              </Link>
            ) : (
              <div className="mt-1.5 space-y-1.5">
                {node.children.map((child) => (
                  <CommentNode key={child.id} node={child} postId={postId} postSlug={postSlug} postLocked={postLocked} />
                ))}
              </div>
            ))}
        </div>
      </div>
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="comment" targetId={node.id} />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={deletingAsMod ? "Remove this comment?" : "Delete your comment?"}
        description={
          deletingAsMod
            ? "It will show as [removed] — replies underneath stay visible."
            : "It will show as [deleted] and can't be restored — replies underneath stay visible."
        }
        confirmLabel={deletingAsMod ? "Remove comment" : "Delete comment"}
        requireReason={deletingAsMod}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// ── Tree wrapper ────────────────────────────────────────────────────────────
export function CommentTree({
  comments,
  postId,
  postSlug,
  postLocked,
  rootId,
}: {
  comments: ForumComment[];
  postId: string;
  postSlug: string;
  postLocked: boolean;
  rootId?: string | null;
}) {
  const tree = useMemo(() => buildCommentTree(comments), [comments]);
  const roots = rootId ? tree.filter((t) => t.id === rootId || t.rootCommentId === rootId) : tree;

  if (roots.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">No comments yet — share your take.</p>;
  }
  return (
    <div className="space-y-2">
      {roots.map((node) => (
        <CommentNode key={node.id} node={node} postId={postId} postSlug={postSlug} postLocked={postLocked} />
      ))}
    </div>
  );
}
